import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTULO_STATUS_PARCELA, formatarReais, type StatusParcela } from '@reducee/contracts';
import { useState } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { Botao, Celula, Linha, Pilula, Tabela, data, type Tom } from './Superficie.tsx';

type Parcela = {
  id: string;
  contratoId: string;
  contato: { id: string; nome: string };
  produto: string;
  numero: number;
  totalParcelas: number;
  vencimento: string;
  valorCentavos: number;
  status: StatusParcela;
  situacao: StatusParcela | 'VENCIDA';
  pagaEm: string | null;
  observacao: string | null;
};

const TOM_DA_SITUACAO: Record<StatusParcela | 'VENCIDA', Tom> = {
  PAGA: 'sucesso',
  PAGA_COM_ATRASO: 'alerta',
  PENDENTE: 'neutro',
  VENCIDA: 'erro',
  ISENTA: 'informacao',
};

/**
 * Parcelas mensais dos contratos.
 *
 * A primeira parcela nasce paga junto com a confirmação do 1º boleto — por
 * isso ela não oferece o botão de registrar pagamento: registrá-la de novo
 * duplicaria o evento que definiu a vigência.
 */
export function ParcelasDoContrato(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['parcelas'],
    queryFn: () => chamarApi<Parcela[]>('/parcelas'),
  });

  const registrar = useMutation({
    mutationFn: (id: string) =>
      chamarApi(`/parcelas/${id}`, {
        metodo: 'PATCH',
        corpo: { status: 'PAGA', pagaEm: new Date().toISOString().slice(0, 10) },
      }),
    onSuccess: async () => {
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['parcelas'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível registrar.');
    },
  });

  const parcelas = consulta.data ?? [];

  return (
    <div>
      {erro ? (
        <p role="alert" className="mb-3 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      <Tabela
        colunas={['Vencimento', 'Cliente', 'Produto', 'Parcela', 'Valor', 'Status', 'Pago em', 'Ação']}
        vazio="As parcelas aparecem após o pagamento do primeiro boleto."
        carregando={consulta.isLoading}
      >
        {parcelas.map((parcela) => (
          <Linha key={parcela.id}>
            <Celula>{data(parcela.vencimento)}</Celula>
            <Celula>
              <strong>{parcela.contato.nome}</strong>
            </Celula>
            <Celula>{parcela.produto}</Celula>
            <Celula>
              {parcela.numero}/{parcela.totalParcelas}
            </Celula>
            <Celula>{formatarReais(parcela.valorCentavos)}</Celula>
            <Celula>
              <Pilula tom={TOM_DA_SITUACAO[parcela.situacao]}>
                {parcela.situacao === 'VENCIDA' ? 'Vencida' : ROTULO_STATUS_PARCELA[parcela.status]}
              </Pilula>
            </Celula>
            <Celula>{data(parcela.pagaEm)}</Celula>
            <Celula>
              {parcela.numero === 1 ? (
                <span className="text-xs text-texto-suave">1º boleto</span>
              ) : parcela.status === 'PAGA' || parcela.status === 'PAGA_COM_ATRASO' ? (
                <span className="text-xs text-texto-suave">—</span>
              ) : (
                <Botao onClick={() => registrar.mutate(parcela.id)}>Registrar pagamento</Botao>
              )}
            </Celula>
          </Linha>
        ))}
      </Tabela>
    </div>
  );
}
